import { useDialogFocusTrap } from "@base/model/hooks/useDialogFocusTrap";
import cn from "classnames";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface FullPageSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /**
   * opt-in. 지정하면 시트를 이름 있는 dialog로 표시하고 초점 이동·Tab 가두기·닫을 때 초점 복귀를 적용한다.
   * PC에서 panel로 배치하는 사용처(className으로 폭 제한)는 함께 지정한다.
   */
  ariaLabel?: string;
}

export function FullPageSheet({ isOpen, onClose, children, className, ariaLabel }: FullPageSheetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocusTrap(panelRef, ariaLabel !== undefined && isOpen);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (isOpen) {
      el.removeAttribute("inert");
    } else {
      el.setAttribute("inert", "");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const content = (
    <div
      ref={containerRef}
      className={cn(
        "fixed inset-0 z-overlay",
        "transition-opacity duration-300",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bg-black/50",
          "transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0",
        )}
        // 모바일은 시트가 화면 전체를 덮어 backdrop이 가려지므로, 실제로는 PC에서 폭을 제한한 panel 바깥 클릭에만 반응한다.
        role="button"
        tabIndex={-1}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onClose();
          }
        }}
        aria-label="Close sheet"
      />

      <div
        ref={panelRef}
        // 닫혀 있을 때는 dialog로 노출하지 않는다 (닫힌 시트도 DOM에 남아 있다).
        role={ariaLabel && isOpen ? "dialog" : undefined}
        aria-modal={ariaLabel && isOpen ? true : undefined}
        aria-label={isOpen ? ariaLabel : undefined}
        tabIndex={ariaLabel ? -1 : undefined}
        className={cn(
          "absolute right-0 bottom-0 left-0 outline-none",
          "flex flex-col bg-white",
          "transition-transform duration-300 ease-out",
          isOpen ? "translate-y-0" : "translate-y-full",
          className,
        )}
        style={{
          height: "100dvh",
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="overflow-y-auto overscroll-contain flex-1">{children}</div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export function useFullPageSheet(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle };
}
